import { Notification } from '@progress/kendo-react-notification';
import { Fade } from '@progress/kendo-react-animation';
import { NotificationType, ToastState } from '../../models/notification.model';
import { Button } from '@progress/kendo-react-buttons';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { APP_INSIGHTS_EVENT_TYPE } from 'common/utils/telemetry';
import { useAppInsights } from 'common/hooks/useAppInsights';
import { sanitizeHtml } from 'common/utils/sanitize';

const NOTIFICATION_TYPE_TO_APP_INSIGHTS_TYPE: Record<NotificationType, APP_INSIGHTS_EVENT_TYPE> = {
  [NotificationType.NONE]: APP_INSIGHTS_EVENT_TYPE.INFO,
  [NotificationType.ERROR]: APP_INSIGHTS_EVENT_TYPE.ERROR,
  [NotificationType.WARNING]: APP_INSIGHTS_EVENT_TYPE.WARNING,
  [NotificationType.INFO]: APP_INSIGHTS_EVENT_TYPE.INFO,
  [NotificationType.SUCCESS]: APP_INSIGHTS_EVENT_TYPE.SUCCESS,
};

const TRACKABLE_NOTIFICATION_TYPES: NotificationType[] = [
  NotificationType.ERROR,
  NotificationType.WARNING,
];

interface ToastProps {
  visible: boolean;
  onClose: (id: string) => void;
}

export default function Toast({
  id,
  title,
  text,
  buttonLabel,
  buttonLink,
  type = NotificationType.NONE,
  visible,
  onClose,
  newTab,
}: ToastState & ToastProps) {
  const navigate = useNavigate();
  const { trackEvent } = useAppInsights();

  useEffect(() => {
    if (visible && TRACKABLE_NOTIFICATION_TYPES.includes(type)) {
      trackEvent({
        type: NOTIFICATION_TYPE_TO_APP_INSIGHTS_TYPE[type],
        name: `Toast - ${title ?? text}`,
        properties: {
          id,
          title,
          text,
          buttonLabel,
          type,
        },
      });
    }
  }, [visible, id, title, text, buttonLabel, type, trackEvent]);

  return (
    <Fade>
      {visible && (
        <Notification
          type={{ style: type, icon: false }}
          closable={true}
          onClose={() => (onClose ? onClose(id) : null)}
        >
          <div className="toast-content-container">
            <div className="toast-text">
              {title ? <span className="toast-title">{title}</span> : null}
              {/* eslint-disable-next-line react/no-danger -- Sanitized with DOMPurify */}
              <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(text) }} />
            </div>
            {buttonLink && buttonLabel && (
              <div className="toast-button">
                <Button
                  size="small"
                  rounded="full"
                  fillMode={'outline'}
                  onClick={() =>
                    newTab
                      ? window.open(`${window.location.origin}${buttonLink}`)
                      : navigate(buttonLink)
                  }
                >
                  {buttonLabel}
                </Button>
              </div>
            )}
          </div>
        </Notification>
      )}
    </Fade>
  );
}
